import React, { useState, useEffect } from 'react';
import './App.css';
import { useLanguage } from './LanguageContext';
import translations from './translations';
import CheckIn from './components/CheckIn';
import CheckOut from './components/CheckOut';
import Dashboard from './components/Dashboard';
import OrgSetup from './components/OrgSetup';
import Login from './components/Login';
import AdminDashboard from './components/AdminDashboard';
import Register from './components/Register';
import Reports from './components/Reports';
import SignUp from './components/SignUp';
import ClassroomView from './components/ClassroomView';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import OrgSettings from './components/OrgSettings';
import Upgrade from './components/Upgrade';
import { supabase } from './supabaseClient';

function App() {
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];
  const [screen, setScreen] = useState('org');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [staffUser, setStaffUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isQRScan, setIsQRScan] = useState(false);
  const [isNewUser, setIsNewUser] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('signup') === 'true';
  });
  const [isClassroomView, setIsClassroomView] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('classroom') === 'true';
  });
  const [isPasswordReset, setIsPasswordReset] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('reset') === 'true';
  });
  const [isUpgradeSuccess] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('upgrade') === 'success';
  });

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setStaffUser(session.user);
        fetchProfile(session.user.id);
      }
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const upgrade = params.get('upgrade');
    const orgId = params.get('org');

    if (upgrade === 'success' && orgId) {
      supabase
        .from('organizations')
        .update({ plan: 'paid' })
        .eq('id', orgId)
        .then(() => {
          alert('🎉 Welcome to SafeRoster Pro! Your plan has been upgraded.');
        });
    }
  }, []);

  const fetchProfile = async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (!error) setProfile(data);
  };

  const handleSelectOrg = (org, fromQR = false) => {
    setSelectedOrg(org);
    setIsQRScan(fromQR);
    setScreen('home');
  };

  const handleLogin = async (user) => {
    setStaffUser(user);
    await fetchProfile(user.id);
    setScreen('dashboard');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setStaffUser(null);
    setProfile(null);
    setScreen('home');
  };

  if (isClassroomView) {
    return (
      <ClassroomView orgId={new URLSearchParams(window.location.search).get('org')} />
    );
  }

  if (isPasswordReset) {
    return (
      <div className="app-container">
        <div className="card">
          <ResetPassword onComplete={() => {
            setIsPasswordReset(false);
            setScreen('login');
          }} />
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div style={{ position: 'fixed', top: '12px', right: '12px', zIndex: 9999 }}>
        <button
          onClick={toggleLanguage}
          style={{
            background: '#1a56db', color: 'white', border: 'none',
            borderRadius: '20px', padding: '6px 14px', fontSize: '13px',
            fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 8px rgba(26,86,219,0.3)'
          }}
        >
          🌐 {t.langToggle}
        </button>
      </div>
      <div className="card">

        {screen === 'org' && isNewUser && (
          <SignUp
            onSignUpComplete={(org, action) => {
              if (action === 'login') {
                setIsNewUser(false);
                setScreen('login');
              } else {
                setIsNewUser(false);
                setSelectedOrg(org);
                setScreen('home');
              }
            }}
          />
        )}

        {screen === 'org' && !isNewUser && (
          <>
            <OrgSetup onSelectOrg={handleSelectOrg} />
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              {!staffUser ? (
                <button className="btn btn-gray btn-small" onClick={() => setScreen('login')}>
                  {t.staffLogin}
                </button>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                  <span style={{ color: '#888', fontSize: '14px', alignSelf: 'center' }}>
                    {t.loggedInAs} {staffUser.email}
                  </span>
                  <button className="btn btn-gray btn-small" onClick={handleLogout}>
                    {t.logout}
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {screen === 'home' && (
          <>
            <div className="header">
              <div className="app-logo">🛡️ Safe<span>Roster</span></div>
              <p>
                {selectedOrg?.name}
                <span className="badge">{selectedOrg?.type}</span>
              </p>
            </div>

            {isQRScan ? (
              <div className="menu-grid">
                <div className="menu-card" onClick={() => setScreen('register')}>
                  <div className="icon">👶</div>
                  <h3>Check-In</h3>
                </div>
                <div className="menu-card" onClick={() => setScreen('checkout')}>
                  <div className="icon">👋</div>
                  <h3>Check-Out</h3>
                </div>
              </div>
            ) : (
              <div className="menu-grid">
                <div className="menu-card" onClick={() => setScreen('register')}>
                  <div className="icon">👶</div>
                  <h3>{t.checkIn}</h3>
                </div>
                <div className="menu-card" onClick={() => setScreen('checkout')}>
                  <div className="icon">👋</div>
                  <h3>{t.checkOut}</h3>
                </div>
                <div className="menu-card" onClick={() => staffUser ? setScreen('dashboard') : setScreen('login')}>
                  <div className="icon">📋</div>
                  <h3>{t.staffDashboard}</h3>
                </div>
                {profile?.role !== 'staff' && (
                  <div className="menu-card" onClick={() => staffUser ? setScreen('reports') : setScreen('login')}>
                    <div className="icon">📊</div>
                    <h3>{t.reports}</h3>
                  </div>
                )}
                {profile?.role !== 'staff' && (
                  <div className="menu-card" onClick={() => staffUser ? setScreen('admin') : setScreen('login')}>
                    <div className="icon">🛠️</div>
                    <h3>{t.adminPanel}</h3>
                  </div>
                )}
                {profile?.role !== 'staff' && (
                  <div className="menu-card" onClick={() => staffUser ? setScreen('settings') : setScreen('login')}>
                    <div className="icon">⚙️</div>
                    <h3>{t.settings}</h3>
                  </div>
                )}
                <div className="menu-card" onClick={() => {
                  const url = `${window.location.origin}${window.location.pathname}?classroom=true&org=${selectedOrg?.id}`;
                  window.open(url, '_blank');
                }}>
                  <div className="icon">📺</div>
                  <h3>{t.classroomView}</h3>
                </div>
                <div className="menu-card" onClick={() => setScreen('org')}>
                  <div className="icon">🏫</div>
                  <h3>{t.switchOrg}</h3>
                </div>
                {profile?.role !== 'staff' && (
                  <div className="menu-card" onClick={() => staffUser ? setScreen('upgrade') : setScreen('login')}
                    style={{ gridColumn: '1 / -1', background: 'linear-gradient(135deg, #f0f4ff, #e8f0fe)', borderColor: '#1a56db' }}>
                    <div className="icon">⭐</div>
                    <h3 style={{ color: '#1a56db' }}>{t.upgradePro}</h3>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {screen === 'login' && (
          <>
            <div className="top-bar">
              <button className="btn btn-gray" onClick={() => setScreen('org')}>{t.back}</button>
            </div>
            <Login onLogin={handleLogin} />
            <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '14px' }}>
              <span
                style={{ color: '#1a56db', cursor: 'pointer', fontWeight: '600' }}
                onClick={() => setScreen('forgot')}
              >
                {t.forgotPassword}
              </span>
            </p>
          </>
        )}

        {screen === 'forgot' && (
          <ForgotPassword onBack={() => setScreen('login')} />
        )}

        {screen === 'checkin' && (
          <>
            <div className="top-bar">
              <button className="btn btn-gray" onClick={() => setScreen('home')}>← Back</button>
            </div>
            <CheckIn orgId={selectedOrg?.id} />
          </>
        )}

        {screen === 'checkout' && (
          <>
            <div className="top-bar">
              <button className="btn btn-gray" onClick={() => setScreen('home')}>← Back</button>
            </div>
            <CheckOut orgId={selectedOrg?.id} />
          </>
        )}

        {screen === 'register' && (
          <Register
            orgId={selectedOrg?.id}
            onBack={() => setScreen('home')}
            onSuccess={(msg) => {
              setScreen('home');
              alert(msg);
            }}
          />
        )}

        {screen === 'dashboard' && (
          <>
            <div className="top-bar">
              <button className="btn btn-gray" onClick={() => setScreen('home')}>← Back</button>
              <button className="btn btn-gray" onClick={handleLogout}>Logout</button>
            </div>
            <Dashboard orgId={selectedOrg?.id} />
          </>
        )}

        {screen === 'reports' && staffUser && profile?.role !== 'staff' && (
          <>
            <div className="top-bar">
              <button className="btn btn-gray" onClick={() => setScreen('home')}>← Back</button>
            </div>
            <Reports orgId={selectedOrg?.id} plan={selectedOrg?.plan || 'free'} />
          </>
        )}

        {screen === 'settings' && staffUser && selectedOrg && profile?.role !== 'staff' && (
          <OrgSettings
            orgId={selectedOrg?.id}
            onBack={() => setScreen('home')}
          />
        )}

        {screen === 'upgrade' && staffUser && profile?.role !== 'staff' && (
          <Upgrade
            orgId={selectedOrg?.id}
            orgName={selectedOrg?.name}
            onBack={() => setScreen('home')}
            onUpgradeSuccess={() => setScreen('home')}
          />
        )}

        {screen === 'admin' && staffUser && profile?.role !== 'staff' && (
          <AdminDashboard
            user={staffUser}
            onBack={() => setScreen('home')}
          />
        )}

      </div>
    </div>
  );
}

export default App;